import { useCallback } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useCurrentAccountOrders } from 'src/hooks/query/accountTradingData/useOrders';

const MAX_ORDER_COUNT = 32;
const MIN_SCALED_ORDER_COUNT = 2;

interface Params {
	updateStateFromCustomInput: (value: string) => void;
	updateStateFromPreset: (value: string) => void;
	updateCustomInputValue: (value: string) => void;
}

export default function useScaledOrderCountInputHandlers({
	updateStateFromCustomInput,
	updateStateFromPreset,
	updateCustomInputValue,
}: Params) {
	const setDriftStore = useDriftStore((s) => s.set);

	const updateStore = useCallback(
		(value: number) => {
			setDriftStore((s) => {
				s.tradeForm.scaledOrders.orderCount = value;
			});
		},
		[setDriftStore]
	);

	const openOrders = useCurrentAccountOrders();
	const openOrderCount = openOrders.length;

	const handleCustomInputChange = useCallback(
		(value: string) => {
			const parsedVal = parseInt(value, 10);

			//  If the value `isNaN` or less than the min, we skip updating the store so that
			//  the previous valid value remains. We also don't force-replace the user's input,
			// as we want to be able to e.g. fully delete the input or type in `1` -> `15`.
			if (isNaN(parsedVal) || parsedVal < MIN_SCALED_ORDER_COUNT) {
				updateCustomInputValue(value);
				return;
			}

			// If the value is greater than the remaining order count, we update it to the
			// remaining order count. Here, we do force-replace the user's input, as it seems
			// more acceptible in this case.
			const remainingOrderCount = MAX_ORDER_COUNT - openOrderCount;
			if (parsedVal > remainingOrderCount) {
				updateStateFromCustomInput(remainingOrderCount.toString());
				updateStore(remainingOrderCount);
				return;
			}

			// Otherwise, their value is valid and we can use it to update.
			updateStateFromCustomInput(value);
			updateStore(parsedVal);
		},
		[
			openOrderCount,
			updateStateFromCustomInput,
			updateCustomInputValue,
			updateStore,
		]
	);

	const handlePresetClick = useCallback(
		(value: number) => {
			updateStateFromPreset(value.toString());
			updateStore(value);
		},
		[updateStateFromPreset, updateStore]
	);

	return {
		handleCustomInputChange,
		handlePresetClick,
	};
}
